import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { uploadWithProgress } from "@/lib/downloadWithProgress";
import { Upload, X, FileText, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ScanButton } from "@/components/ScanButton";
import { CameraButton } from "@/components/CameraButton";

const REQUIRED_KINDS = [
  { kind: "carta_identita", label: "Carta d'identità", required: true, hint: "Fronte/retro o pdf unico" },
  { kind: "tessera_sanitaria", label: "Tessera Sanitaria o Codice Fiscale", required: true, hint: "Fronte" },
  { kind: "carta_circolazione", label: "Carta di circolazione veicolo da assicurare", required: false, hint: "Fronte/retro (libretto)" },
  { kind: "carta_circolazione_bersani", label: "Carta di circolazione veicolo familiare (Bersani)", required: false, hint: "Solo se applichi Bersani / RC Familiare" },
];

const MAX_MB = 10;

/**
 * Props:
 *   onChange(attachmentIds: string[])
 *   showBersani: bool — mostra/nasconde la riga Bersani
 *   isAuto: bool — mostra/nasconde la riga "carta circolazione veicolo da assicurare"
 */
export const QuoteAttachmentsUploader = ({ onChange, onStatusChange, showBersani = false, isAuto = false }) => {
  const [files, setFiles] = useState({}); // kind → {id, filename}
  const [uploading, setUploading] = useState(null);
  const inputsRef = useRef({});

  const triggerInput = (kind) => inputsRef.current[kind]?.click();

  // Emette ids + lista required mancanti (per messaggi "manca X" lato parent)
  const emit = (nextFiles) => {
    const ids = Object.values(nextFiles).map((f) => f.id);
    onChange?.(ids);
    const visibleRequired = REQUIRED_KINDS.filter((r) => {
      if (!r.required) return false;
      if (r.kind === "carta_circolazione" && !isAuto) return false;
      if (r.kind === "carta_circolazione_bersani" && !showBersani) return false;
      return true;
    });
    const missing = visibleRequired.filter((r) => !nextFiles[r.kind]).map((r) => r.label);
    onStatusChange?.({ missing });
  };

  const handleFile = async (kind, fileObj) => {
    if (!fileObj) return;
    if (fileObj.size > MAX_MB * 1024 * 1024) {
      toast.error(`File troppo grande (max ${MAX_MB} MB)`);
      return;
    }
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const { data } = await uploadWithProgress({
        label: `📎 ${fileObj.name} → allegato preventivo`,
        run: (cfg) => api.post(`/my/quote-attachments?kind=${encodeURIComponent(kind)}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: cfg.onUploadProgress,
        }),
      });
      const next = { ...files, [kind]: { id: data.id, filename: data.filename, size: data.size } };
      setFiles(next);
      emit(next);
      toast.success("Allegato caricato");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(null); }
  };

  const remove = async (kind) => {
    const f = files[kind];
    if (!f) return;
    try {
      await api.delete(`/my/quote-attachments/${f.id}`);
      const next = { ...files };
      delete next[kind];
      setFiles(next);
      emit(next);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Riemette quando cambiano isAuto/showBersani (cambiano le righe required visibili)
  useEffect(() => { emit(files); }, [isAuto, showBersani]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = REQUIRED_KINDS.filter((r) => {
    if (r.kind === "carta_circolazione" && !isAuto) return false;
    if (r.kind === "carta_circolazione_bersani" && !showBersani) return false;
    return true;
  });

  return (
    <div className="space-y-2.5" data-testid="quote-attachments">
      <p className="text-[11px] uppercase tracking-wide text-blue-700 font-semibold border-b border-blue-100 pb-1">Documenti da allegare</p>
      <p className="text-[11px] text-slate-500">Formato accettato: PDF, JPG, PNG, WEBP — max {MAX_MB} MB per file.</p>
      {rows.map((r) => {
        const f = files[r.kind];
        return (
          <div key={r.kind} className="flex items-center gap-3 border border-slate-200 rounded-md px-3 py-2 bg-white" data-testid={`att-${r.kind}`}>
            <input
              ref={(el) => { inputsRef.current[r.kind] = el; }}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              onChange={(e) => handleFile(r.kind, e.target.files?.[0])}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <FileText className="h-3.5 w-3.5 text-slate-400" />
                <span className="truncate">{r.label}</span>
                {r.required && <span className="text-[10px] text-red-500 font-bold">*</span>}
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5">{r.hint}</p>
            </div>
            {f ? (
              <div className="flex items-center gap-2">
                <span className="text-xs inline-flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span className="truncate max-w-[140px]">{f.filename}</span>
                </span>
                <button
                  type="button"
                  data-testid={`remove-${r.kind}`}
                  onClick={() => remove(r.kind)}
                  className="text-slate-400 hover:text-red-600"
                  title="Rimuovi"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <ScanButton
                  testid={`scan-${r.kind}`}
                  label="Importa da scanner"
                  variant="ghost"
                  disabled={uploading === r.kind}
                  onFile={(f) => handleFile(r.kind, f)}
                />
<CameraButton testid="cam-camera" label="Foto da fotocamera" variant="outline" disabled={uploading === r.kind} onFile={(f) => handleFile(r.kind, f)} />
                <button
                  type="button"
                  data-testid={`upload-${r.kind}`}
                  onClick={() => triggerInput(r.kind)}
                  disabled={uploading === r.kind}
                  className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-700 disabled:opacity-50"
                >
                  {uploading === r.kind ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  Carica
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
